import { storeToRefs } from "pinia"
import { computed, inject } from "vue"
import { useCookies } from "@vueuse/integrations/useCookies"
import { useFavicon, useTitle } from "@vueuse/core"

/* STORES */
import { useConfigStore } from "@/stores/config"
import { useUserStore } from "@/stores/user"
import type { Translate } from "@/plugins/i18n/types"

/* CONSTANTS */
import { COOKIES } from "@/config/constants"

/* UTILS */
import { hexToHSL, hexToRgb } from "@/lib/utils"

export const useBranding = () => {
  const configStore = useConfigStore()
  const t = inject("t") as Translate

  const cookies = useCookies()
  const afAuthSession = cookies.get(COOKIES.AUTH_SESSION)
  const branding = afAuthSession?.branding ?? cookies.get(COOKIES.BRANDING)

  const userStore = useUserStore()
  const { isResellerAcc } = storeToRefs(userStore)

  const icon = useFavicon()
  const title = useTitle()

  const primaryColor = isResellerAcc.value ? "#455C51" : "#ff2f92"
  const secondaryColor = isResellerAcc.value ? "#f5f0e8" : "#f59f00"

  const style = computed(() => ({
    "--af-branding-cta": `hsl(${hexToHSL(branding?.color?.cta ?? primaryColor)})`,
    "--af-branding-cta-hover": `hsl(${hexToHSL(
      branding?.color?.cta ?? (isResellerAcc.value ? primaryColor : "#e6006d"),
      {
        lightness: -10,
      }
    )})`,
    "--af-branding-theme": branding?.color?.theme ?? primaryColor,
    "--af-branding-border":
      branding?.color?.cta ?? (isResellerAcc.value ? "#f1f3f4" : "#ffd5e9"),
    "--af-gradient-start-rgb": hexToRgb(
      branding?.background_colors?.primary ?? primaryColor
    ),
    "--af-gradient-end-rgb": hexToRgb(
      branding?.background_colors?.secondary ?? secondaryColor
    ),
    "--af-gradient-start": `hsl(${hexToHSL(branding?.background_colors?.primary ?? primaryColor)})`,
    "--af-gradient-end": `hsl(${hexToHSL(branding?.background_colors?.secondary ?? secondaryColor)})`,
    "--af-gradient-start-rgb-10": `rgba(${hexToRgb(branding?.background_colors?.primary ?? primaryColor, 0.1)})`,
    "--af-gradient-end-rgb-10": `rgba(${hexToRgb(branding?.background_colors?.secondary ?? secondaryColor, 0.1)})`,
    "--af-gradient-start-rgb-20": `rgba(${hexToRgb(branding?.background_colors?.primary ?? primaryColor, 0.2)})`,
    "--af-gradient-end-rgb-20": `rgba(${hexToRgb(branding?.background_colors?.secondary ?? secondaryColor, 0.2)})`,
    "--af-gradient-start-rgb-30": `rgba(${hexToRgb(branding?.background_colors?.primary ?? primaryColor, 0.3)})`,
    "--af-gradient-end-rgb-30": `rgba(${hexToRgb(branding?.background_colors?.secondary ?? secondaryColor, 0.3)})`,
    "--af-gradient-start-rgb-50": `rgba(${hexToRgb(branding?.background_colors?.primary ?? primaryColor, 0.5)})`,
    "--af-gradient-end-rgb-50": `rgba(${hexToRgb(branding?.background_colors?.secondary ?? secondaryColor, 0.5)})`,
    "--af-gradient-start-rgb-65": `rgba(${hexToRgb(branding?.background_colors?.primary ?? primaryColor, 0.65)})`,
    "--af-gradient-end-rgb-65": `rgba(${hexToRgb(branding?.background_colors?.secondary ?? secondaryColor, 0.65)})`,
    "--af-branding-text": branding?.text_color ?? "#2D2F33",
    "--af-branding-heading": branding?.header_color ?? "#484D51",
    "--af-branding-accent": branding?.color?.cta ?? primaryColor,
    "--af-branding-border-avatar": branding?.color?.theme ?? "#fce2b2",
    "--af-branding-progressbar": branding?.color?.theme ?? "#4ebac7",
    "--af-branding-text-accent": branding?.color?.theme ?? "#029caf",
    "--bg-checkbox": branding?.color?.cta ?? primaryColor,
    "--color-background-checkbox-checked": branding?.color?.cta ?? primaryColor,
    "--color-icon-primary": branding?.color?.cta ?? primaryColor,
  }))

  // Apply the branding CSS custom properties on the document body.
  const applyBrandingStyle = () => {
    const root = document.body.style
    Object.entries(style.value).forEach(([key, value]) => {
      root.setProperty(key, value)
    })
  }

  // Set the favicon to the branding logo (if any).
  const applyBrandingFavicon = () => {
    if (branding?.logo) {
      icon.value = `${configStore.urls.cdn_legacy}/${branding.logo}`
    }
  }

  const applyUnbrandedTitle = () => {
    title.value = t("scan.meta.title_plain")
  }

  return {
    branding,
    style,
    applyBrandingStyle,
    applyBrandingFavicon,
    applyUnbrandedTitle,
  }
}
